<template>
  <v-container fluid class="pa-6">
    <div class="flex justify-between items-center mb-6">
      <h1 class="text-h5 font-weight-bold text-grey-darken-4">Painel Administrativo</h1>
      <v-btn color="primary" @click="reloadDashboard">
        <v-icon start>mdi-refresh</v-icon>
        Atualizar
      </v-btn>
    </div>

    <!-- Indicadores principais -->
    <v-row dense>
      <v-col v-for="card in stats" :key="card.title" cols="12" sm="6" md="3">
        <v-card class="hover:shadow-lg transition-all duration-300 rounded-2xl" :color="card.color" dark>
          <v-card-text class="flex flex-col items-start justify-between h-full py-6">
            <div class="flex items-center space-x-3">
              <v-icon size="36">{{ card.icon }}</v-icon>
              <h3 class="text-lg font-semibold">{{ card.title }}</h3>
            </div>
            <p class="text-3xl font-bold mt-4">{{ card.value }}</p>
            <p v-if="card.subtitle" class="text-sm mt-1 text-gray-200">{{ card.subtitle }}</p>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Seção de métricas complementares -->
    <v-card class="mt-8 pa-4 rounded-2xl elevation-4">
      <v-card-title class="text-h6 font-bold mb-4">
        Visão Geral dos Anúncios
      </v-card-title>
      <v-card-text>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div class="flex flex-col items-center justify-center bg-gray-50 rounded-xl p-4 shadow-sm">
            <span class="text-2xl font-bold text-blue-600">{{ indicators.approvedAds }}</span>
            <span class="text-gray-700">Aprovados</span>
          </div>
          <div class="flex flex-col items-center justify-center bg-gray-50 rounded-xl p-4 shadow-sm">
            <span class="text-2xl font-bold text-amber-600">{{ indicators.inAnalysisAds }}</span>
            <span class="text-gray-700">Em Análise</span>
          </div>
          <div class="flex flex-col items-center justify-center bg-gray-50 rounded-xl p-4 shadow-sm">
            <span class="text-2xl font-bold text-gray-700">{{ indicators.expiredAds }}</span>
            <span class="text-gray-700">Expirados</span>
          </div>
        </div>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script setup>
import { ref, onMounted } from "vue";
import api from "@/services/api";
import { useToastStore } from "@/store/toast";
import { useLoadingStore } from "@/store/loadingStore";

const toastStore = useToastStore();
const loadingStore = useLoadingStore();

const stats = ref([]);
const indicators = ref({
  approvedAds: 0,
  inAnalysisAds: 0,
  expiredAds: 0,
});

const loadDashboard = async () => {
  loadingStore.show();
  try {
    const { data } = await api.get("/dashboard"); // 🔹 endpoint no backend
    const d = data.data;

    // cards principais
    stats.value = [
      {
        title: "Clientes Ativos",
        value: d.active_clients,
        color: "blue-darken-3",
        icon: "mdi-account-group",
      },
      {
        title: "Pagamentos Aprovados",
        value: Number(d.approved_payments).toLocaleString("pt-BR", {
          style: "currency",
          currency: "BRL",
        }),
        color: "green-darken-3",
        icon: "mdi-cash-multiple",
      },
      {
        title: "Anúncios Ativos",
        value: d.active_ads,
        color: "purple-darken-3",
        icon: "mdi-bullhorn",
      },
      {
        title: "Visualizações Totais",
        value: Number(d.total_views).toLocaleString("pt-BR"),
        color: "pink-darken-3",
        icon: "mdi-eye",
      },
      {
        title: "Cupons Ativos",
        value: d.active_coupons,
        color: "amber-darken-3",
        icon: "mdi-ticket-percent",
      },
      {
        title: "Planos Ativos",
        value: d.active_subscriptions,
        color: "teal-darken-3",
        icon: "mdi-card-account-details-star",
      },
      {
        title: "Receita (30 dias)",
        value: Number(d.revenue_30d).toLocaleString("pt-BR", {
          style: "currency",
          currency: "BRL",
        }),
        color: "indigo-darken-3",
        icon: "mdi-chart-line",
      },
    ];

    indicators.value = {
      approvedAds: d.approved_ads,
      inAnalysisAds: d.in_analysis_ads,
      expiredAds: d.expired_ads,
    };
  } catch (error) {
    console.error(error);
    toastStore.showToast({
      message: "Erro ao carregar indicadores do painel.",
      color: "error",
    });
  } finally {
    loadingStore.hide();
  }
};

const reloadDashboard = () => {
  loadDashboard();
};

onMounted(loadDashboard);
</script>

<style scoped>
.v-card-text h3 {
  font-size: 0.95rem;
  /* título do card */
}

.v-card-text p.text-3xl {
  font-size: 1.7rem;
  /* número principal */
}

.text-h5 {
  font-size: 1.1rem !important;
  /* título do painel */
}

.hover\:shadow-lg:hover {
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
}
</style>
